import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '@/config';
import { redirectToAuthLogin } from '@/api/apiHealth';
import { getAuthHeaders } from '@/api/workosAuth';
import { PageLoading } from '@/components/ui/loading';

const API_UNAVAILABLE_MESSAGE =
  'Sign-in is temporarily unavailable. Our servers may be undergoing maintenance — please try again in a few minutes.';

/**
 * Login is not a real page: check for existing session first, then redirect to backend /auth/login.
 * WorkOS handles the form, SSO, etc. If URL has ?error=... (e.g. from callback), show a short message.
 *
 * Post-logout behaviour: when from_logout=1 (app logout) or signed_out=1 (WorkOS return_to redirect),
 * we DO NOT auto-redirect to /auth/login after the session check fails. Instead we show a sign-in
 * button so the user must explicitly start a new SSO flow. This prevents SSO providers (Google,
 * Microsoft, Okta) from silently re-authenticating the user without their intent.
 */
function LoginSignInPanel({
  message,
  onSignIn,
  signInBusy,
}: {
  message: string;
  onSignIn: () => void;
  signInBusy: boolean;
}) {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-4 bg-nb-bg p-8">
      <p className="text-sm text-muted-foreground text-center">{message}</p>
      <button
        type="button"
        onClick={onSignIn}
        disabled={signInBusy}
        className="px-4 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
      >
        {signInBusy ? 'Checking…' : 'Sign in'}
      </button>
    </div>
  );
}

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const error = searchParams.get('error');
  const fromLogout = searchParams.get('from_logout') === '1';
  const signedOut = searchParams.get('signed_out') === '1';
  const isPostLogout = fromLogout || signedOut;
  const [checkingSession, setCheckingSession] = React.useState(true);
  const [logoutUrlDone, setLogoutUrlDone] = React.useState(false);
  const [apiUnavailable, setApiUnavailable] = React.useState(false);
  const [signInBusy, setSignInBusy] = React.useState(false);

  const beginSignIn = React.useCallback(async (context: Record<string, string>) => {
    setSignInBusy(true);
    setApiUnavailable(false);
    const result = await redirectToAuthLogin(context);
    if (result === 'unavailable') {
      setApiUnavailable(true);
      setSignInBusy(false);
    }
  }, []);

  React.useEffect(() => {
    if (!fromLogout || error) return;
    let cancelled = false;
    (async () => {
      const stored = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('pending_workos_logout') : null;
      if (stored) {
        if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem('pending_workos_logout');
        if (!cancelled && (stored.startsWith('http://') || stored.startsWith('https://'))) {
          window.location.href = stored;
          return;
        }
      }
      try {
        const res = await fetch(`${API_BASE_URL}/auth/logout-url`, { credentials: 'include' });
        const data = res.ok ? await res.json().catch(() => ({})) : {};
        const logoutUrl = data?.logout_url;
        if (!cancelled && typeof logoutUrl === 'string' && (logoutUrl.startsWith('http://') || logoutUrl.startsWith('https://'))) {
          window.location.href = logoutUrl;
          return;
        }
      } catch {
        // ignore
      }
      if (!cancelled) setLogoutUrlDone(true);
    })();
    return () => { cancelled = true; };
  }, [fromLogout, error]);

  React.useEffect(() => {
    if (error) {
      setCheckingSession(false);
      return;
    }
    if (fromLogout && !logoutUrlDone) return;
    let cancelled = false;
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await fetch(`${API_BASE_URL}/auth/me`, { credentials: 'include', headers });
        if (!cancelled && res.ok) {
          window.location.href = '/';
          return;
        }
      } catch {
        // ignore
      }
      if (!cancelled) {
        setCheckingSession(false);
        if (!isPostLogout) {
          const result = await redirectToAuthLogin({ trigger: 'auto_redirect' });
          if (!cancelled && result === 'unavailable') {
            setApiUnavailable(true);
          }
        }
      }
    })();
    return () => { cancelled = true; };
  }, [error, fromLogout, logoutUrlDone, signedOut, isPostLogout]);

  const handleSignIn = () => {
    void beginSignIn({ trigger: 'sign_in_button' });
  };

  if (apiUnavailable) {
    return (
      <LoginSignInPanel
        message={API_UNAVAILABLE_MESSAGE}
        onSignIn={handleSignIn}
        signInBusy={signInBusy}
      />
    );
  }

  if (error) {
    const message =
      error === 'auth_failed' ? 'Sign-in failed. Please try again.' :
      error === 'no_token' ? 'Sign-in did not complete. Please try again.' :
      'Something went wrong. Please try again.';
    return (
      <LoginSignInPanel message={message} onSignIn={handleSignIn} signInBusy={signInBusy} />
    );
  }

  if (isPostLogout && !checkingSession) {
    return (
      <LoginSignInPanel
        message="You have been signed out."
        onSignIn={handleSignIn}
        signInBusy={signInBusy}
      />
    );
  }

  const isSigningOut = fromLogout && !logoutUrlDone;
  const message = isSigningOut ? 'Signing out…' : checkingSession ? 'Checking session…' : 'Redirecting to sign in…';
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-nb-bg">
      <PageLoading message={message} />
    </div>
  );
}
